import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient
} from "@tanstack/react-query"
import { useMemo } from "react"

import {
  addPeople,
  captureLinkedinConnections,
  connectCirclesProvider,
  getDialerSnoozes,
  setDialerSnooze,
  type IDialerSnooze,
  fetchPersonMutuals,
  addPersonTag,
  addToList,
  addUploadToList,
  createList,
  deleteList,
  moveToList,
  removeFromList,
  enrichPeople,
  getIcpSettings,
  getImportStatus,
  getLists,
  getCrmLists,
  importCrmLists,
  refetchCrmList,
  CRM_SYNC_TERMINAL,
  getPerson,
  getPersonActivity,
  getCirclesProviderStatus,
  getPersonCircle,
  getPersonEngagement,
  getPersonTasks,
  getPersonMemberships,
  getPersonOpportunities,
  getPersonProfile,
  getTalkingPoints,
  getWarmConnects,
  listPeople,
  recommendPeople,
  importPresign,
  searchCompanies,
  searchPeople,
  startImport,
  updateIcpSettings,
  uploadImportFile,
  type ListPeopleArgs,
  type IPeopleList,
  ingestCirclesConnectionsPage
} from "~/api/people-os"
import useGlobalStore from "~/store/global"
import { resolveUnifiedOsAction, undoUnifiedOsAction } from "~/api/unified-os"
import { ITokenInfo } from "~/types/global"
import type {
  IIcpSettings,
  IPeopleFilters,
  IPeopleListResponse,
  IPersonActivity,
  IPersonOS,
  IPersonTask
} from "~/types/people-os"

// Canonical LinkedIn URL for cross-source identity matching (a search/candidate
// row and its promoted champion carry different ids but the same profile URL).
function normLinkedin(url?: string | null) {
  if (!url) return ""
  return String(url)
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "")
    .trim()
}

// Write freshly enriched contact details into EVERY cached people-os surface so
// an enrich triggered anywhere (a People-list cell, Person 360, a search row)
// shows up everywhere that person is on screen.
//
// This is a cache WRITE, not an invalidate, and that is deliberate. The enrich
// endpoint only persists what its store can hold: champion records keep email
// and phone, but m_people records (every Recommended candidate) have no phone
// column, so the number comes back in the response and is never written down.
// Invalidating would refetch those rows and blank the phone the user just paid
// for. Champion rows are patched with the same values the server persisted, so
// nothing drifts there either.
//
// Matching is by person_id AND by normalized linkedin_url: the same human is
// keyed differently across surfaces (champion uuid in a list, m_people bigint
// in Recommended, an Apollo id in search), and the LinkedIn URL is the one
// identity all three share.
function applyEnrichmentToCaches(
  qc: QueryClient,
  enriched: { person_id: string; email?: string; phone?: string }[] = []
) {
  const withValues = enriched.filter((e) => e.email || e.phone)
  if (!withValues.length) return

  const byId = new Map<string, { email?: string; phone?: string }>()
  for (const e of withValues) {
    byId.set(String(e.person_id), { email: e.email, phone: e.phone })
  }

  // Pass 1: learn the LinkedIn URL of every enriched person from whatever
  // cache entry already holds them, so pass 2 can match the same human under a
  // different id.
  const byUrl = new Map<string, { email?: string; phone?: string }>()
  const learn = (p: IPersonOS | undefined) => {
    if (!p) return
    const vals = byId.get(String(p.id))
    const url = normLinkedin(p.linkedin_url)
    if (vals && url) byUrl.set(url, vals)
  }
  for (const entry of qc.getQueryCache().findAll({ queryKey: ["people-os"] })) {
    const data = entry.state.data as
      | { people?: IPersonOS[]; person?: IPersonOS }
      | undefined
    data?.people?.forEach(learn)
    learn(data?.person)
  }

  const patch = (p: IPersonOS): IPersonOS => {
    const vals =
      byId.get(String(p.id)) ?? byUrl.get(normLinkedin(p.linkedin_url))
    if (!vals) return p
    const email = vals.email || p.email
    const phone = vals.phone || p.phone
    if (email === p.email && phone === p.phone) return p
    return { ...p, email, phone, enriched: p.enriched || !!email }
  }

  qc.setQueriesData<{ people?: IPersonOS[]; person?: IPersonOS }>(
    { queryKey: ["people-os"] },
    (old) => {
      // Runs over every people-os cache entry, including ones with neither
      // shape (lists, import status, memberships) — those fall through
      // untouched.
      if (!old) return old
      let next = old
      if (old.people?.length) {
        const people = old.people.map(patch)
        if (people.some((p, i) => p !== old.people![i])) {
          next = { ...next, people }
        }
      }
      if (old.person) {
        const person = patch(old.person)
        if (person !== old.person) next = { ...next, person }
      }
      return next
    }
  )
}

// Optimistic cache surgery: people just added to a list must disappear from the
// Recommended tab IMMEDIATELY (the backend already excludes list members from
// Recommended — this front-runs the refetch, which then confirms the truth).
// Matches by champion id AND by linkedin_url/email, because a not-yet-promoted
// candidate (search/recommended row) gets a NEW champion id server-side.
function scrubAddedFromRecommended(
  qc: QueryClient,
  people: Partial<IPersonOS>[] = [],
  championIds: string[] = []
) {
  const ids = new Set<string>([
    ...championIds,
    ...(people.map((p) => p.id).filter(Boolean) as string[])
  ])
  const urls = new Set(
    people.map((p) => normLinkedin(p.linkedin_url)).filter(Boolean)
  )
  const emails = new Set(
    people.map((p) => (p.email || "").toLowerCase().trim()).filter(Boolean)
  )
  qc.setQueriesData<IPeopleListResponse>(
    {
      queryKey: ["people-os", "list"],
      // Only pages whose query args are the RECOMMENDED scope — People-list
      // pages must keep the freshly added rows.
      predicate: (q) =>
        (q.queryKey[2] as ListPeopleArgs | undefined)?.scope === "recommended"
    },
    (old) => {
      if (!old?.people?.length) return old
      const kept = old.people.filter((p) => {
        if (ids.has(p.id)) return false
        const url = normLinkedin(p.linkedin_url)
        if (url && urls.has(url)) return false
        const email = (p.email || "").toLowerCase().trim()
        if (email && emails.has(email)) return false
        return true
      })
      const removed = old.people.length - kept.length
      if (!removed) return old
      return {
        ...old,
        people: kept,
        page_info: old.page_info
          ? {
              ...old.page_info,
              total_size: Math.max(0, (old.page_info.total_size || 0) - removed)
            }
          : old.page_info
      }
    }
  )
}

// People list for a scope (Recommended triage vs People list execution).
// The edge forwards read RPCs to ProspectOS as GET; data is dummy this phase.
export function useListPeople(args: ListPeopleArgs, enabled = true) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "list", args],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await listPeople(tokenInfo, args)
      return res.data
    },
    enabled: !!tokenInfo && enabled,
    retry: false,
    // Scope/list switching must feel instant: serve the cached page for five
    // minutes (background prefetch keeps it warm) instead of refetching per
    // click. Add/import/ICP mutations invalidate explicitly.
    staleTime: 5 * 60 * 1000
  })
}

// Same as useListPeople, but for a checkbox-style multi-select filter where
// more than one list can be picked at once. The listPeople endpoint only
// ever takes one list_id per call (see ListPeopleArgs) — there's no known
// batch/array form on the backend it calls — so this fires one query per
// selected list via useQueries (React Query dedupes/caches each by its own
// queryKey, same as calling useListPeople that many times individually)
// and merges the results into a single deduplicated array of person ids.
const LIST_ALL_PAGE_SIZE = 200
const LIST_ALL_MAX_PAGES = 25

export function useMultiListPeople(listIds: string[], enabled = true) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null

  const results = useQueries({
    queries: listIds.map((list_id) => ({
      queryKey: ["people-os", "list-all", { scope: "list", list_id }],
      queryFn: async () => {
        if (!tokenInfo) throw new Error("Missing token info")
        const people: IPersonOS[] = []
        const seenPageTokens = new Set<string>()
        let page_token: string | undefined
        let pages = 0
        do {
          const res = await listPeople(tokenInfo, {
            scope: "list",
            list_id,
            page_size: LIST_ALL_PAGE_SIZE,
            page_token
          })
          people.push(...res.data.people)
          pages += 1
          const next = res.data.page_info.has_more
            ? res.data.page_info.next_page_token
            : undefined
          // Two ways this walk can fail to terminate, both of which hang the
          // modal's filter behind a request loop rather than surfacing an
          // error: the backend repeating a page_token it already handed out,
          // and a list large enough that draining it sequentially is itself
          // the outage. Stop on either and filter on what arrived — a
          // truncated list filter shows too many calls, which is visible and
          // recoverable; a wedged modal is neither.
          page_token =
            next && !seenPageTokens.has(next) && pages < LIST_ALL_MAX_PAGES
              ? next
              : undefined
          if (next) seenPageTokens.add(next)
        } while (page_token)
        return { people }
      },
      enabled: !!tokenInfo && enabled,
      retry: false,
      staleTime: 5 * 60 * 1000
    }))
  })

  const isLoading = results.some((r) => r.isLoading)
  // The rows themselves, not just their ids. The dialer builds its queue from
  // these: a list is one page of 8 in the grid, and a queue built from that
  // page can only ever dial 8 of a 20-person list.
  const people = useMemo(() => {
    const seen = new Set<string>()
    const out: IPersonOS[] = []
    for (const r of results) {
      for (const p of r.data?.people ?? []) {
        const key = String(p.id)
        if (seen.has(key)) continue
        seen.add(key)
        out.push(p)
      }
    }
    return out
  }, [results])
  const personIds = useMemo(() => people.map((p) => String(p.id)), [people])
  // Which SELECTED list each person belongs to — the first match, in the
  // same order as listIds. A person in more than one selected list only
  // needs one to show in the modal's List column; results[i] lines up with
  // listIds[i] since useQueries preserves query order.
  const listIdByPersonId = useMemo(() => {
    const map: Record<string, string> = {}
    results.forEach((r, i) => {
      for (const p of r.data?.people ?? []) {
        const key = String(p.id)
        if (!(key in map)) map[key] = listIds[i]
      }
    })
    return map
  }, [results, listIds])

  return { people, personIds, isLoading, listIdByPersonId }
}

// Prefetch the member pages of EVERY list in the background so opening a list
// serves from cache instead of a fresh (slow) fetch per click.
export function usePrefetchListPeople() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return (argsList: ListPeopleArgs[]) => {
    if (!tokenInfo) return
    for (const args of argsList) {
      qc.prefetchQuery({
        queryKey: ["people-os", "list", args],
        queryFn: async () => {
          const res = await listPeople(tokenInfo, args)
          return res.data
        },
        staleTime: 5 * 60 * 1000
      })
    }
  }
}

// Recommended-people pool for a set of accounts.
export function useRecommendPeople(
  args: { account_ids: string[]; page_size?: number; page_token?: string },
  enabled = true
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "recommend", args],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await recommendPeople(tokenInfo, args)
      return res.data
    },
    enabled: !!tokenInfo && enabled && args.account_ids.length > 0,
    retry: false
  })
}

// A single person for the Person 360 Overview. `linkedinUrl` is the disambiguating
// hint the clicked row carries (the id alone is source-dependent and not always
// resolvable server-side); it is part of the query key so two people can never
// share one cache entry.
export function useGetPerson(id: string | null, linkedinUrl?: string | null) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "person", id, linkedinUrl ?? null],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPerson(tokenInfo, id as string, linkedinUrl)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Related opportunities for the Person 360 card, fetched SEPARATELY from the
// person and in PARALLEL with it (same route id + linkedin_url hint, so it
// does not wait for the person to resolve first).
//
// The LLM relevance match behind this takes 29-33s. While it ran inside
// GetPerson it exceeded the backend's 20s ProspectOS read budget, so the whole
// page failed with "people service error" and no person rendered at all — the
// match then completed into a response nobody was holding. On its own query the
// page paints immediately and only this one card waits.
//
// retry: false like its siblings: a 30s call is not one to repeat, and the
// endpoint already degrades to an empty list rather than erroring.
export function usePersonOpportunities(
  id: string | null,
  linkedinUrl?: string | null
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "opportunities", id, linkedinUrl ?? null],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonOpportunities(
        tokenInfo,
        id as string,
        linkedinUrl
      )
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Rapid-enriched profile for the Person 360 Experience/About card. A LIVE
// Rapid call on every page open — no staleTime on purpose. Errors (including
// the pre-deploy 404) leave data undefined; the card hides itself.
export function usePersonProfile(id: string | null) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "profile", id],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonProfile(tokenInfo, id as string)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Account-contextual talking points, fetched when Person 360 opens. Empty or
// failed responses fall back to the local deterministic points in the page.
export function useTalkingPoints(id: string | null, accountId: string) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "talking-points", id, accountId],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getTalkingPoints(tokenInfo, id as string, accountId)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Lists this person belongs to + their tags (Lists & segments card).
export function usePersonMemberships(id: string | null) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "memberships", id],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonMemberships(tokenInfo, id as string)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Add a tag to a person (inline "+ Add tag" on Person 360). Refetches the
// memberships card on success. A 400 (person not in a list yet) is surfaced by
// the caller with the API's error message.
export function usePersonTasks(id: string | null, linkedinUrl?: string | null) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "tasks", id, linkedinUrl ?? null],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonTasks(tokenInfo, id as string, linkedinUrl)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

export function usePersonEngagement(
  id: string | null,
  linkedinUrl?: string | null
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "engagement", id, linkedinUrl ?? null],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonEngagement(
        tokenInfo,
        id as string,
        linkedinUrl
      )
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Cursor-paged activity feed. Pages are flattened and de-duplicated by id: the
// first page guarantees a few rows per source regardless of age, so a row it
// returned can come round again on the page whose cursor reaches it.
export function usePersonActivity(
  id: string | null,
  linkedinUrl?: string | null
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const query = useInfiniteQuery({
    queryKey: ["people-os", "activity", id, linkedinUrl ?? null],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonActivity(
        tokenInfo,
        id as string,
        linkedinUrl,
        {
          before: pageParam
        }
      )
      return res.data
    },
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.next_before ? lastPage.next_before : null,
    enabled: !!tokenInfo && !!id,
    retry: false
  })
  const activities = useMemo(() => {
    const seen = new Set<string>()
    const out: IPersonActivity[] = []
    for (const page of query.data?.pages ?? []) {
      for (const a of page.activities) {
        if (seen.has(a.id)) continue
        seen.add(a.id)
        out.push(a)
      }
    }
    return out
  }, [query.data])
  return {
    activities,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage
  }
}

export function usePersonCircle(
  id: string | null,
  linkedinUrl?: string | null
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "circle", id, linkedinUrl ?? null],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getPersonCircle(tokenInfo, id as string, linkedinUrl)
      return res.data
    },
    enabled: !!tokenInfo && !!id,
    retry: false
  })
}

// Finds the people the viewer and this person both know.
//
// A mutation, not a query: it costs a LinkedIn request against a rate limiter
// the network capture already strains, so it runs when a rep asks for it and
// never on render. Invalidates the circle so the card re-reads the ranked
// paths with the mutuals folded in.
export function useFetchPersonMutuals() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      personId: string
      cookiesMap: Record<string, string>
      linkedinUrl?: string | null
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await fetchPersonMutuals(
        tokenInfo,
        args.personId,
        args.cookiesMap,
        args.linkedinUrl
      )
      return res.data
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "circle"] })
    }
  })
}

// People this rep has snoozed. Long-lived: a snooze has no expiry, so the
// only thing that changes it is this rep toggling one, and the mutation below
// invalidates it.
export function useDialerSnoozes() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "dialer-snoozes", tokenInfo?.user_id ?? null],
    queryFn: async () => {
      const empty = {
        champion_ids: [] as string[],
        snoozes: [] as IDialerSnooze[]
      }
      if (!tokenInfo) return empty
      const res = await getDialerSnoozes(tokenInfo)
      const data = res.data ?? empty
      // An API that predates snoozed_until answers champion_ids only. Those
      // people are snoozed with no end date, which is exactly what they meant
      // before the column existed.
      return {
        champion_ids: data.champion_ids ?? [],
        snoozes:
          data.snoozes ??
          (data.champion_ids ?? []).map((id) => ({
            champion_id: id,
            snoozed_until: null
          }))
      }
    },
    enabled: !!tokenInfo,
    staleTime: 5 * 60 * 1000,
    retry: false
  })
}

export function useSetDialerSnooze() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      championId: string
      snoozed: boolean
      reason?: string
      /** YYYY-MM-DD, inclusive. Omitted or null = no expiry. */
      snoozedUntil?: string | null
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await setDialerSnooze(
        tokenInfo,
        args.championId,
        args.snoozed,
        args.reason,
        args.snoozedUntil
      )
      return res.data
    },
    // Optimistic: snooze is pressed mid-call and the row has to leave the
    // callable side immediately, not after a round trip.
    onMutate: async (args) => {
      const key = ["people-os", "dialer-snoozes", tokenInfo?.user_id ?? null]
      type Cache = { champion_ids: string[]; snoozes: IDialerSnooze[] }
      const prev = qc.getQueryData<Cache>(key)
      qc.setQueryData<Cache>(key, (old) => {
        const ids = new Set(old?.champion_ids ?? [])
        const rest = (old?.snoozes ?? []).filter(
          (x) => x.champion_id !== args.championId
        )
        if (args.snoozed) {
          ids.add(args.championId)
          return {
            champion_ids: [...ids],
            snoozes: [
              ...rest,
              {
                champion_id: args.championId,
                snoozed_until: args.snoozedUntil ?? null
              }
            ]
          }
        }
        ids.delete(args.championId)
        return { champion_ids: [...ids], snoozes: rest }
      })
      return { prev, key }
    },
    onError: (_e, _args, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "dialer-snoozes"] })
    }
  })
}

export function useCaptureLinkedinConnections() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      cookiesMap: Record<string, string>
      connectLinkedinUrl?: string
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await captureLinkedinConnections(
        tokenInfo,
        args.cookiesMap,
        args.connectLinkedinUrl
      )
      return res.data
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "circle"] })
    }
  })
}

// ── Circles V2 ──────────────────────────────────────────────────────────────

// Registers the rep's LinkedIn session and starts the import, in one call.
//
// Takes the same cookiesMap useCaptureLinkedinConnections does, from the same
// extension. Whether an import actually started is in the response rather than
// assumed: a rep who already synced this month is told when the next one runs
// instead of silently re-paging their LinkedIn.
export function useIngestCirclesConnections() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      start: number
      connections: {
        firstName: string
        lastName: string
        headline: string
        publicIdentifier: string
      }[]
      more: boolean
      connector_linkedin_url: string
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await ingestCirclesConnectionsPage(tokenInfo, args)
      return res.data
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "circle"] })
    }
  })
}

export function useConnectCirclesProvider() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { cookiesMap: Record<string, string> }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await connectCirclesProvider(tokenInfo, args.cookiesMap)
      return res.data
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "circle"] })
    }
  })
}

// The rep's own Circles link: has it been made, and is an import running.
//
// `enabled` and `pollMs` are separate because the two questions have different
// costs. Whether a rep has EVER connected is needed on every People screen, to
// decide whether to offer them the button - that is one cheap fetch. Watching
// an import PROGRESS is only needed while one is in flight. Passing pollMs = 0
// fetches once and then leaves it alone.
export function useCirclesProviderStatus(enabled: boolean, pollMs = 5000) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "circles-provider-status"],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getCirclesProviderStatus(tokenInfo)
      return res.data
    },
    enabled: !!tokenInfo && enabled,
    refetchInterval: enabled && pollMs > 0 ? pollMs : false,
    // The rep is watching a loader, so a stale cached answer would keep it up
    // after the import has finished.
    staleTime: 0,
    retry: false
  })
}

export function useResolvePersonTask() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { actionId: string; resolved: boolean }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = args.resolved
        ? await resolveUnifiedOsAction(tokenInfo, args.actionId)
        : await undoUnifiedOsAction(tokenInfo, args.actionId)
      return res.data?.data
    },
    onMutate: async (args) => {
      const snapshots = qc.getQueriesData({
        queryKey: ["people-os", "tasks"]
      })
      qc.setQueriesData<{ tasks?: IPersonTask[] }>(
        { queryKey: ["people-os", "tasks"] },
        (old) =>
          old?.tasks
            ? {
                ...old,
                tasks: old.tasks.map((t) =>
                  t.id === args.actionId
                    ? { ...t, status: args.resolved ? "resolved" : "open" }
                    : t
                )
              }
            : old
      )
      return { snapshots }
    },
    onError: (_err, _args, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, data)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "tasks"] })
      qc.invalidateQueries({ queryKey: ["unified-os-feed"] })
    }
  })
}

export function useAddPersonTag() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { personId: string; name: string }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await addPersonTag(tokenInfo, args.personId, args.name)
      return res.data
    },
    onSuccess: (_, args) => {
      qc.invalidateQueries({
        queryKey: ["people-os", "memberships", args.personId]
      })
    }
  })
}

// Manual People Search over the global graph (Search source).
//
// `runId` is bumped by the caller on every Search click and is part of the
// query key, so a search ALWAYS issues a request when the user asks for one.
// Without it the 10-minute staleTime silently swallowed re-searches: any filter
// the wire mapping does not carry (Function / Industry / Employee count) left
// `args` byte-identical, react-query served the previous result, and no network
// call was made at all.
export function useSearchPeople(
  args: {
    query: string
    filters?: IPeopleFilters
    page_size?: number
    page_token?: string
  },
  enabled = true,
  runId = 0
) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "search", args, runId],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await searchPeople(tokenInfo, args)
      return res.data
    },
    enabled:
      !!tokenInfo &&
      enabled &&
      (args.query.trim().length > 0 ||
        Boolean(args.filters?.predicates?.length) ||
        Boolean(args.filters?.account_ids?.length)),
    retry: false,
    // Reopening the search sheet must show the previous results, not re-run
    // the (Apollo-backed) search: serve cache for 10 minutes.
    staleTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}

// Company typeahead for People Search. Keyed on the trimmed query so each
// distinct term is cached, and disabled under 2 characters so a typeahead does
// not fire an Apollo call per keystroke from the first letter.
export function useCompanySearch(query: string) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const q = query.trim()
  return useQuery({
    queryKey: ["people-os", "company-search", q],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await searchCompanies(tokenInfo, q)
      return res.data
    },
    enabled: !!tokenInfo && q.length >= 2,
    retry: false,
    staleTime: 10 * 60 * 1000
  })
}

// Warm-connects (Circles) for a set of target LinkedIn URLs.
export function useGetWarmConnects(targetLinkedinUrls: string[]) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "warm-connects", targetLinkedinUrls],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getWarmConnects(tokenInfo, targetLinkedinUrls)
      return res.data
    },
    enabled: !!tokenInfo && targetLinkedinUrls.length > 0,
    retry: false
  })
}

// The user's saved ICP settings (Node edge capability). Dummy-safe: falls back
// to null when the endpoint is unavailable this phase.
export function useIcpSettings(enabled = true) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "icp-settings"],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getIcpSettings(tokenInfo)
      return res.data
    },
    enabled: !!tokenInfo && enabled,
    retry: false
  })
}

// Save (upsert) ICP settings; invalidates the recommendation queries so the
// Recommended pool refreshes with the new ICP once Feed 3 is live.
export function useUpdateIcpSettings() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (settings: IIcpSettings) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await updateIcpSettings(tokenInfo, settings)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "icp-settings"] })
      qc.invalidateQueries({ queryKey: ["people-os", "recommend"] })
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
    }
  })
}

// ── Promotion + Lists (FR-4 / ENG-2693) ──────────────────────────────────────

// Promote people into the user's People list (Recommended "Add to list").
// Invalidates the list queries so both scopes refresh.
export function useAddPeople() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (people: Partial<IPersonOS>[]) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await addPeople(tokenInfo, people)
      throwOnPromoteErrors(res.data)
      return res.data
    },
    onSuccess: (_, people) => {
      // Drop the added people from every cached Recommended page first (instant
      // UI), THEN invalidate so the server truth follows in the background.
      scrubAddedFromRecommended(qc, people)
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
    }
  })
}

// The user's lists with member counts.
export function useLists(enabled = true) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useQuery({
    queryKey: ["people-os", "lists"],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getLists(tokenInfo)
      return res.data
    },
    enabled: !!tokenInfo && enabled,
    // Poll only while a CRM import is still working. `partial` is TERMINAL, not
    // an error — it means every member was fetched but some CRM contacts have no
    // matching person yet, which is the normal resting state on a portal whose
    // contact sync has not caught up. Treating it as in-progress would poll
    // forever, so the terminal set is shared with the renderer rather than
    // re-listed here.
    refetchInterval: (query) => {
      const lists = (query.state.data as { lists?: IPeopleList[] } | undefined)
        ?.lists
      const working = lists?.some(
        (l) =>
          l.crm_sync_status && !CRM_SYNC_TERMINAL.includes(l.crm_sync_status)
      )
      return working ? 10_000 : false
    }
  })
}

// Page param for useCrmLists: undefined for the first page of either mode,
// then either an offset (search mode, `q` set — the server queries HubSpot
// LIVE) or a keyset cursor (cached mode, `q` empty — server-cached,
// newest-first). Never both at once, since the two modes never mix.
type ICrmListsPageParam =
  | { offset: number }
  | { cursor_ts: string; cursor_id: string }
  | undefined

// The connected CRM's lists, for the import picker. Not polled: the picker is
// opened on demand and the CRM's own list of lists changes rarely.
//
// `q` drives BOTH the query key (a new search term is a new infinite query,
// not a page of the previous one) and which paging mode the server picks —
// see ICrmListsPageParam. The query key keeps the ["people-os", "crm-lists"]
// prefix un-nested (as {q}, not a new segment before it) so
// useImportCrmLists/useRefetchCrmList's prefix invalidation still matches
// every search variant.
export function useCrmLists(q: string, enabled = true) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
    isFetching,
    error,
    dataUpdatedAt
  } = useInfiniteQuery({
    queryKey: ["people-os", "crm-lists", { q }],
    initialPageParam: undefined as ICrmListsPageParam,
    queryFn: async ({ pageParam }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getCrmLists(tokenInfo, {
        q: q || undefined,
        offset:
          pageParam && "offset" in pageParam ? pageParam.offset : undefined,
        cursor_ts:
          pageParam && "cursor_ts" in pageParam
            ? pageParam.cursor_ts
            : undefined,
        cursor_id:
          pageParam && "cursor_id" in pageParam
            ? pageParam.cursor_id
            : undefined,
        limit: 100
      })
      return res.data
    },
    getNextPageParam: (lastPage): ICrmListsPageParam => {
      if (!lastPage.has_more) return undefined
      if (lastPage.mode === "search") {
        return lastPage.next_offset !== undefined
          ? { offset: lastPage.next_offset }
          : undefined
      }
      return lastPage.next_cursor_ts !== undefined &&
        lastPage.next_cursor_id !== undefined
        ? {
            cursor_ts: lastPage.next_cursor_ts,
            cursor_id: lastPage.next_cursor_id
          }
        : undefined
    },
    enabled: !!tokenInfo && enabled,
    retry: false,
    staleTime: 60 * 1000
  })

  const lastPage = data?.pages[data.pages.length - 1]

  return {
    data: {
      lists: data?.pages.flatMap((page) => page.lists) ?? [],
      total: lastPage?.total,
      portal_id: lastPage?.portal_id
    },
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
    isFetching,
    error,
    // When the catalogue itself was last pulled, for the drawer's "Pulled 2m
    // ago" stamp. React Query's own value, so it survives a refetch that
    // returns identical data.
    dataUpdatedAt
  }
}

// Import the selected CRM lists. The lists appear immediately and empty; members
// are filled in by the background drain, which useLists' poll then picks up.
export function useImportCrmLists() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (crmListIds: string[]) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await importCrmLists(tokenInfo, crmListIds)
      return res.data
    },
    onSuccess: () => {
      // Targeted, not the whole ["people-os"] namespace — see useCreateList.
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      // The picker's "already imported" flags are now stale.
      qc.invalidateQueries({ queryKey: ["people-os", "crm-lists"] })
    }
  })
}

// Re-snapshot an already-imported CRM list.
export function useRefetchCrmList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await refetchCrmList(tokenInfo, id)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      qc.invalidateQueries({ queryKey: ["people-os", "crm-lists"] })
    }
  })
}

// Create a list (optionally with initial members, or with everyone from a
// completed CSV import via `upload_id`).
// promotePeople reports per-person failures in `errors` and the request still
// answers 200, so onSuccess fired on a response that wrote nothing: a champion
// whose linkedin_url already existed under another urn raised 23505, was
// swallowed as a per-person skip, and the modal reported the people added while
// the list stayed empty. Anything in `errors` is a person who did not land, so
// it is raised here and the caller shows the reason.
//
// `added` is NOT the test. It counts new list memberships, so it is legitimately
// 0 when everyone selected was already in the list, and failing on that would
// turn a harmless re-add into an error.
function throwOnPromoteErrors(res: { errors?: { reason?: string }[] }) {
  const errors = res?.errors ?? []
  if (!errors.length) return
  const reason = errors[0]?.reason || "could not be added"
  throw new Error(
    errors.length === 1 ? reason : `${reason} (and ${errors.length - 1} more)`
  )
}

export function useCreateList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      name: string
      people?: Partial<IPersonOS>[]
      champion_ids?: string[]
      people_ids?: string[]
      upload_id?: string
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await createList(tokenInfo, args)
      throwOnPromoteErrors(res.data)
      return res.data
    },
    onSuccess: (_, args) => {
      // Instant removal from Recommended (both the promoted candidates and the
      // already-known champions), then the usual invalidation.
      scrubAddedFromRecommended(qc, args.people, args.champion_ids)
      // Targeted, NOT the whole ["people-os"] tree. Invalidating the namespace
      // also matched ["people-os", "search", ...], so adding a person to a list
      // from People Search re-ran the Apollo search behind them: the results
      // they were reading through visibly reloaded, and it burned a paid call.
      // Lists, list members and Recommended are the only things an add changes.
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
      qc.invalidateQueries({ queryKey: ["people-os", "recommend"] })
    }
  })
}

// Add members to an existing list.
export function useAddToList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      listId: number
      people?: Partial<IPersonOS>[]
      champion_ids?: string[]
      people_ids?: string[]
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await addToList(tokenInfo, args.listId, {
        people: args.people,
        champion_ids: args.champion_ids,
        people_ids: args.people_ids
      })
      throwOnPromoteErrors(res.data)
      return res.data
    },
    onSuccess: (_, args) => {
      scrubAddedFromRecommended(qc, args.people, args.champion_ids)
      // Targeted, NOT the whole ["people-os"] tree. Invalidating the namespace
      // also matched ["people-os", "search", ...], so adding a person to a list
      // from People Search re-ran the Apollo search behind them: the results
      // they were reading through visibly reloaded, and it burned a paid call.
      // Lists, list members and Recommended are the only things an add changes.
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
      qc.invalidateQueries({ queryKey: ["people-os", "recommend"] })
    }
  })
}

// File every person from a completed CSV import into an existing list (the
// post-import "where should these go?" prompt). Invalidates the whole people-os
// tree so the list counts and the member pages both refresh.
export function useAddUploadToList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { listId: number; upload_id: string }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await addUploadToList(tokenInfo, args.listId, args.upload_id)
      return res.data
    },
    onSuccess: () => {
      // Targeted, NOT the whole ["people-os"] tree. Invalidating the namespace
      // also matched ["people-os", "search", ...], so adding a person to a list
      // from People Search re-ran the Apollo search behind them: the results
      // they were reading through visibly reloaded, and it burned a paid call.
      // Lists, list members and Recommended are the only things an add changes.
      qc.invalidateQueries({ queryKey: ["people-os", "list"] })
      qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
      qc.invalidateQueries({ queryKey: ["people-os", "recommend"] })
    }
  })
}

// Refresh only what a membership change touches (member pages, lists index,
// whole-list ids, a person's memberships). Never the whole ["people-os"]
// namespace: that re-runs People Search's paid Apollo query.
function invalidateListMembership(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["people-os", "list"] })
  qc.invalidateQueries({ queryKey: ["people-os", "list-all"] })
  qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
  qc.invalidateQueries({ queryKey: ["people-os", "memberships"] })
}

// Soft-remove members from a list.
export function useRemoveFromList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { listId: number; champion_ids: string[] }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await removeFromList(tokenInfo, args.listId, {
        champion_ids: args.champion_ids
      })
      return res.data
    },
    onSuccess: () => invalidateListMembership(qc)
  })
}

// Move members from one list to another (existing `to_list_id` or new `name`).
export function useMoveToList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      listId: number
      champion_ids: string[]
      to_list_id?: number
      name?: string
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await moveToList(tokenInfo, args.listId, {
        champion_ids: args.champion_ids,
        to_list_id: args.to_list_id,
        name: args.name
      })
      return res.data
    },
    onSuccess: () => invalidateListMembership(qc)
  })
}

// Soft-delete a list.
export function useDeleteList() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { listId: number }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await deleteList(tokenInfo, args.listId)
      return res.data
    },
    onSuccess: () => {
      invalidateListMembership(qc)
      // A deleted CRM-imported list must show as importable again in the
      // CRM list picker.
      qc.invalidateQueries({ queryKey: ["people-os", "crm-lists"] })
    }
  })
}

// Bulk enrich (email/phone/both) — quota-gated server-side.
export function useEnrichPeople() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      person_ids: string[]
      mode: "email" | "phone" | "both"
    }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await enrichPeople(tokenInfo, args)
      return res.data
    },
    // Patch the returned values into every cached surface instead of
    // invalidating — see applyEnrichmentToCaches for why a refetch would lose
    // the phone number on Recommended (m_people) rows.
    onSuccess: (data) => {
      applyEnrichmentToCaches(qc, data?.enriched)
    }
  })
}

// Phone enrichment is ASYNCHRONOUS at the provider. enrichPeople answers as
// soon as it has dispatched, so anyone whose lookup is still running comes back
// in `unenriched` with a not-yet-settled reason - and the bulk bar reported
// that verbatim as "not found". Clicking Enrich on the same person a moment
// later worked, because the single-person path (useResolveCallPhone below) has
// always polled for the late arrival. This is ENG-4371: the bulk path simply
// never learned to wait.
//
// Polling is per person, because the batch lookup that exists reads m_people by
// numeric id and People OS rows are champion uuids. That caps how many can be
// chased at once - beyond the cap the enrichment still completes server side,
// it just lands on the next list refresh rather than in this toast, which the
// caller says out loud rather than calling them missing.
const PHONE_POLL_MAX_PENDING = 25

export function useBulkEnrichPhones() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  const { mutateAsync: enrich } = useEnrichPeople()

  return async (personIds: string[]) => {
    const res = await enrich({ person_ids: personIds, mode: "phone" })
    const found = new Map<string, string>()
    for (const p of res?.enriched ?? []) {
      if (p.phone) found.set(String(p.person_id), String(p.phone))
    }

    // Anything the provider has already ruled on stays ruled on. Only an
    // unsettled reason means "still working".
    const pending = (res?.unenriched ?? [])
      .filter((u) => !PHONE_SETTLED_REASON.test(String(u.reason ?? "")))
      .map((u) => String(u.person_id))
      .filter((id) => !found.has(id))

    const chase = pending.slice(0, PHONE_POLL_MAX_PENDING)
    const outstanding = new Set(chase)
    const deadline = Date.now() + PHONE_POLL_BUDGET_MS

    while (outstanding.size && Date.now() < deadline && tokenInfo) {
      await new Promise((r) => setTimeout(r, PHONE_POLL_INTERVAL_MS))
      const landed: { person_id: string; phone: string }[] = []
      await Promise.all(
        [...outstanding].map(async (id) => {
          const fresh = await getPerson(tokenInfo, id).catch(() => null)
          const phone = fresh?.data?.person?.phone
          if (phone) {
            found.set(id, phone)
            outstanding.delete(id)
            landed.push({ person_id: id, phone })
          }
        })
      )
      if (landed.length) applyEnrichmentToCaches(qc, landed)
    }

    return {
      found: found.size,
      requested: personIds.length,
      settledEmpty: personIds
        .map(String)
        .filter((id) => !found.has(id) && !outstanding.has(id)),
      stillSearching:
        outstanding.size + Math.max(0, pending.length - chase.length),
      reasons: [
        ...new Set(
          (res?.unenriched ?? [])
            .filter((u) => PHONE_SETTLED_REASON.test(String(u.reason ?? "")))
            .map((u) => u.reason)
            .filter(Boolean)
        )
      ].slice(0, 2) as string[],
      remaining: res?.remaining ?? null
    }
  }
}

const PHONE_POLL_INTERVAL_MS = 1500
const PHONE_POLL_BUDGET_MS = 12000
const PHONE_SETTLED_REASON = /quota|disabled|linkedin/i

export function useResolveCallPhone() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  const { mutateAsync: enrich } = useEnrichPeople()

  return async (person: {
    id: string
    phone?: string
    phone_checked?: boolean
    linkedin_url?: string | null
  }): Promise<string | null> => {
    if (person.phone) return person.phone
    if (person.phone_checked) return null
    if (!tokenInfo) return null

    let res
    try {
      res = await enrich({ person_ids: [person.id], mode: "phone" })
    } catch {
      return null
    }

    const direct = res?.enriched?.find(
      (p) => String(p.person_id) === String(person.id)
    )?.phone
    if (direct) return direct

    const reason = res?.unenriched?.find(
      (u) => String(u.person_id) === String(person.id)
    )?.reason
    if (reason && PHONE_SETTLED_REASON.test(reason)) return null

    const deadline = Date.now() + PHONE_POLL_BUDGET_MS
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, PHONE_POLL_INTERVAL_MS))
      const fresh = await getPerson(
        tokenInfo,
        person.id,
        person.linkedin_url
      ).catch(() => null)
      const late = fresh?.data?.person?.phone
      if (late) {
        applyEnrichmentToCaches(qc, [{ person_id: person.id, phone: late }])
        return late
      }
    }
    return null
  }
}

// ── CSV import (ENG-2760) ─────────────────────────────────────────────────────

// Run the full upload chain: presign → PUT to S3 → start the import. Resolves
// to the workflow_id the status poll tracks.
export function useStartImport() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useMutation({
    mutationFn: async (args: { file: File; enrich?: boolean }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const presign = await importPresign(tokenInfo, args.file.name)
      await uploadImportFile(presign.data.upload_url, args.file)
      const started = await startImport(tokenInfo, {
        file_key: presign.data.file_key,
        enrich: args.enrich
      })
      return started.data
    }
  })
}

// Split upload chain for the single-step import modal: presign + PUT to S3 as
// soon as the file is picked, resolving to the file_key startImport needs.
export function useUploadImportFile() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useMutation({
    mutationFn: async (file: File) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const presign = await importPresign(tokenInfo, file.name)
      await uploadImportFile(presign.data.upload_url, file)
      return presign.data.file_key
    }
  })
}

// Start the import for an already-uploaded file_key (second half of the split
// chain). Resolves to the workflow_id the status poll tracks.
export function useStartImportRun() {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  return useMutation({
    mutationFn: async (args: { file_key: string; enrich?: boolean }) => {
      if (!tokenInfo) throw new Error("Missing token info")
      const started = await startImport(tokenInfo, args)
      return started.data
    }
  })
}

// Poll import progress every 2s until the workflow reaches a terminal state.
export function useImportStatus(workflowId: string | null) {
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const qc = useQueryClient()
  return useQuery({
    queryKey: ["people-os", "import", workflowId],
    queryFn: async () => {
      if (!tokenInfo) throw new Error("Missing token info")
      const res = await getImportStatus(tokenInfo, workflowId as string)
      if (res.data.status === "SUCCESS") {
        // Refresh the People list (both the paginated and full-list-walk
        // caches), list sidebar/counts, and recommendations.
        qc.invalidateQueries({ queryKey: ["people-os", "list"] })
        qc.invalidateQueries({ queryKey: ["people-os", "list-all"] })
        qc.invalidateQueries({ queryKey: ["people-os", "lists"] })
        qc.invalidateQueries({ queryKey: ["people-os", "recommend"] })
      }
      return res.data
    },
    enabled: !!tokenInfo && !!workflowId,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      // 10s: imports run in resumable 500-row batches, so a tighter poll adds
      // request churn without telling the user anything new.
      return s === "SUCCESS" || s === "FAILED" ? false : 10000
    }
  })
}
